import { useState } from 'react';
import { Check, HelpCircle } from 'lucide-react';
import { Avatar, Modal, Input, Button } from '@dak/ui';
import { useMembersStore } from '../../stores/members-store';
import { useSettingsStore } from '../../stores/settings-store';
import { SchedulePicker, type ScheduleConfig } from '../shared/SchedulePicker';
import type { ChoreWithAssignments } from '../../types';

interface ChoreEditModalProps {
  chore?: ChoreWithAssignments; // undefined = new chore
  onSave: (
    data: {
      name: string;
      description?: string;
      points: number;
      schedule_type: string;
      interval_days?: number;
      weekly_days?: number[];
      monthly_day?: number;
      assignment_type: 'anyone' | 'everyone';
      times_per_day?: number;
      target_count?: number;
      goal_period?: 'daily' | 'weekly' | 'monthly';
    },
    assigneeIds: string[],
  ) => Promise<void>;
  onClose: () => void;
}

export function ChoreEditModal({ chore, onSave, onClose }: ChoreEditModalProps) {
  const { members } = useMembersStore();
  const { settings } = useSettingsStore();
  const hidePoints = settings?.hide_points ?? false;
  const isEditing = !!chore;

  // Form state
  const [name, setName] = useState(chore?.name ?? '');
  const [description, setDescription] = useState(chore?.description ?? '');
  const [points, setPoints] = useState(chore?.points ?? 1);
  const [schedule, setSchedule] = useState<ScheduleConfig>({
    type: chore?.schedule_type ?? 'daily',
    intervalDays: chore?.interval_days ?? undefined,
    weeklyDays: chore?.weekly_days ?? undefined,
    monthlyDay: chore?.monthly_day ?? undefined,
    targetCount: chore?.target_count ?? undefined,
    goalPeriod: chore?.goal_period ?? undefined,
  });
  const [timesPerDay, setTimesPerDay] = useState(chore?.times_per_day ?? 1);
  const [assigneeIds, setAssigneeIds] = useState<string[]>(
    chore?.assignments.map((a) => a.member.id) ?? [],
  );
  const [assignmentType, setAssignmentType] = useState<'anyone' | 'everyone'>(
    chore?.assignment_type ?? 'everyone',
  );
  const [saving, setSaving] = useState(false);
  const [showAssignmentHelp, setShowAssignmentHelp] = useState(false);

  const toggleAssignee = (memberId: string) => {
    setAssigneeIds((prev) =>
      prev.includes(memberId) ? prev.filter((id) => id !== memberId) : [...prev, memberId],
    );
  };

  const handleSave = async () => {
    if (!name.trim() || assigneeIds.length === 0) return;
    setSaving(true);
    await onSave(
      {
        name: name.trim(),
        description: description.trim() || undefined,
        points,
        schedule_type: schedule.type,
        interval_days: schedule.intervalDays,
        weekly_days: schedule.weeklyDays,
        monthly_day: schedule.monthlyDay,
        // Goals are always "everyone" (per-person, no race)
        assignment_type: schedule.type === 'goal' ? 'everyone' : assignmentType,
        // Multi-daily (only for daily schedule)
        times_per_day: schedule.type === 'daily' ? timesPerDay : 1,
        // Goal fields
        target_count: schedule.type === 'goal' ? schedule.targetCount : undefined,
        goal_period: schedule.type === 'goal' ? schedule.goalPeriod : undefined,
      },
      assigneeIds,
    );
    setSaving(false);
    onClose();
  };

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={isEditing ? 'Edit Chore' : 'New Chore'}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!name.trim() || assigneeIds.length === 0 || saving}
            className="flex-1"
          >
            <Check size={18} />
            {saving ? 'Saving...' : isEditing ? 'Save' : 'Create'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Make bed, Take vitamins"
          maxLength={100}
          autoFocus
        />

        <Input
          label="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Add details..."
          maxLength={200}
        />

        {/* Points - hidden when hide_points is enabled */}
        {!hidePoints && (
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">Points</label>
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setPoints(Math.max(0, points - 1))}
                className="w-10 h-10 text-xl font-medium"
              >
                −
              </Button>
              <span className="text-2xl font-semibold w-12 text-center text-text">{points}</span>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setPoints(Math.min(100, points + 1))}
                className="w-10 h-10 text-xl font-medium"
              >
                +
              </Button>
            </div>
          </div>
        )}

        <SchedulePicker value={schedule} onChange={setSchedule} />

        {/* Times per day - only for daily schedule */}
        {schedule.type === 'daily' && (
          <div className="p-3 bg-accent-light rounded-lg border border-accent">
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Times per day
            </label>
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setTimesPerDay(Math.max(1, timesPerDay - 1))}
                className="w-10 h-10 text-xl font-medium"
              >
                −
              </Button>
              <span className="text-2xl font-semibold w-12 text-center text-text">
                {timesPerDay}
              </span>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setTimesPerDay(Math.min(10, timesPerDay + 1))}
                className="w-10 h-10 text-xl font-medium"
              >
                +
              </Button>
            </div>
            {timesPerDay > 1 && (
              <p className="text-xs text-accent mt-2">
                Creates {timesPerDay} separate checkboxes per day
              </p>
            )}
          </div>
        )}

        {/* Assignees */}
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-2">Assign to</label>
          <div className="flex gap-2 flex-wrap">
            {members.map((member) => (
              <Button
                key={member.id}
                type="button"
                variant="secondary"
                onClick={() => toggleAssignee(member.id)}
                className={`flex items-center gap-2 px-3 py-2 h-auto ${
                  assigneeIds.includes(member.id) ? 'ring-2 ring-accent bg-accent-light' : ''
                }`}
              >
                <Avatar
                  name={member.name}
                  emoji={member.avatar_emoji}
                  color={member.color}
                  size="sm"
                />
                <span className="text-sm text-text">{member.name}</span>
              </Button>
            ))}
          </div>
          {assigneeIds.length === 0 && (
            <p className="text-sm text-warning mt-2">Select at least one person</p>
          )}
        </div>

        {/* Assignment Type - hide for goals (always per-person) */}
        {assigneeIds.length > 1 && schedule.type !== 'goal' && (
          <div className="p-3 bg-surface-raised rounded-lg border border-border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-text-secondary">How should this work?</span>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onClick={() => setShowAssignmentHelp(!showAssignmentHelp)}
              >
                <HelpCircle size={16} />
              </Button>
            </div>

            {showAssignmentHelp && (
              <div className="mb-3 p-2 bg-accent-light rounded text-xs text-accent">
                <p className="font-medium mb-1">Race mode:</p>
                <p className="mb-2">
                  First person to complete wins all the points. Great for motivating quick action!
                </p>
                <p className="font-medium mb-1">Each person:</p>
                <p>Everyone gets their own task and earns points individually.</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setAssignmentType('anyone')}
                className={`p-3 h-auto text-left justify-start ${
                  assignmentType === 'anyone' ? 'bg-warning-light/50 ring-2 ring-warning' : ''
                }`}
              >
                <div>
                  <p className="font-medium text-sm text-text">Race</p>
                  <p className="text-xs text-text-muted mt-0.5">First to finish wins</p>
                </div>
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setAssignmentType('everyone')}
                className={`p-3 h-auto text-left justify-start ${
                  assignmentType === 'everyone' ? 'bg-accent-light ring-2 ring-accent' : ''
                }`}
              >
                <div>
                  <p className="font-medium text-sm text-text">Each person</p>
                  <p className="text-xs text-text-muted mt-0.5">Everyone does it</p>
                </div>
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
